import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import { RefreshCw, Info, X } from 'lucide-react'
import api from 'api/api'
import useStore from 'state/knovStore'
import * as cache from 'state/cache'
import styles from './answer.module.scss'
import errorBubbleStyles from './image-placeholder.module.scss'
import {
    parseActionCommandFromContent,
    confirmActionCommandFee,
    confirmAiPaymentTray,
} from 'utils/actionCommandFeeConfirmation'
import WithTooltip from 'components/shared/WithTooltip'
import { isPublicAiPricingContext } from 'utils/questPricingContext'
import {
    findEffectiveModelForAction,
    findModelByActionTag,
    getPrivateModelCostWithDuration,
    getPublicModelCostWithDuration,
    normalizeActionTag,
    usdToSats,
} from 'state/aiModels'
import { DEFAULT_VIDEO_MODEL } from 'state/defaultAiModels'

function stringifyErrorValue(value) {
    if (value == null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) return value.map(stringifyErrorValue).filter(Boolean).join(' ')
    if (typeof value === 'object') {
        const candidates = [
            value.error,
            value.error_message,
            value.errorMessage,
            value.message,
            value.logs,
            value.detail,
            value.details,
            value.reason || value.status,
            value.prediction_response,
            value.predictionResponse,
            value.response,
            value.payload,
        ]
            .map(stringifyErrorValue)
            .map(text => text.trim())
            .filter(Boolean)
        const preferred = candidates.find(
            candidate => !/^(failed|failure|error|canceled|cancelled)$/i.test(candidate),
        )

        if (preferred || candidates[0]) return preferred || candidates[0]
    }

    try {
        return JSON.stringify(value)
    } catch (error) {
        return String(value)
    }
}

function readableProviderErrorText(value) {
    return String(value || '')
        .replace(/\\n/g, '\n')
        .replace(/\s+/g, ' ')
        .replace(/^(?:prediction failed:\s*)+/i, '')
        .replace(/^(?:async prediction failed:\s*)+/i, '')
        .replace(/^modelerror:\s*/i, '')
        .replace(/\s*\([A-Z]\d{3,}\)(?:\s*\([^)]+\))?:\s*request ID:\s*[\w-]+\s*$/i, '')
        .replace(/\s*request ID:\s*[\w-]+\s*$/i, '')
        .trim()
}

export function getDisplayAiGenerationError(errorMessage) {
    const rawText = stringifyErrorValue(errorMessage)
        .replace(/\\n/g, '\n')
        .replace(/\s+/g, ' ')
        .trim()
    const summaryText = readableProviderErrorText(rawText)
    const isSafetyFilter = /flagged as sensitive|unsafe|safety|content policy|moderation/i.test(
        summaryText || rawText,
    )

    return {
        title: isSafetyFilter ? 'Provider safety filter' : 'Provider error',
        summary:
            summaryText && summaryText.length <= 500 && !/https?:\/\//i.test(summaryText)
                ? summaryText
                : null,
        rawText,
    }
}

function actionPriceUsd(action) {
    const priceLabel = action?.price_label || action?.priceLabel
    if (typeof priceLabel !== 'string') return null
    if (/^\s*free\s*$/i.test(priceLabel)) return 0

    const match = priceLabel.match(/-?\d+(?:\.\d+)?/)
    if (!match) return null

    const price = Number(match[0])
    return Number.isFinite(price) ? price : null
}

function actionGenerationCount(action) {
    const settings = action?.settings || {}
    const generationCount = Number(
        action?.generation_count ||
            action?.generationCount ||
            settings.generation_count ||
            settings.generationCount,
    )

    return Number.isFinite(generationCount) && generationCount > 0 ? generationCount : 1
}

function actionDurationSeconds(action, fallbackDurationSeconds = null) {
    const settings = action?.settings || {}
    const duration = Number(
        settings.duration_seconds ||
            settings.durationSeconds ||
            action?.duration_seconds ||
            action?.durationSeconds ||
            fallbackDurationSeconds,
    )

    return Number.isFinite(duration) && duration > 0 ? duration : null
}

function actionEffectiveFeeTag(action, aiModels) {
    const models = Array.isArray(aiModels) ? aiModels : []
    const settings = action?.settings || {}
    const tag = normalizeActionTag(action?.tag || action?.action_tag || action?.actionTag)

    if (tag === 'extend') {
        const extendModelId = settings.extend_ai_model_id || settings.extendAiModelId || null
        const extendModel = extendModelId ? models.find(model => model?.id === extendModelId) : null

        return normalizeActionTag(
            extendModel?.actionTagName ||
                settings.extend_action_tag ||
                settings.extendActionTag ||
                action?.extend_action_tag ||
                action?.extendActionTag ||
                DEFAULT_VIDEO_MODEL.actionTag,
        )
    }

    const actionModel = findEffectiveModelForAction(models, tag, action)
    return normalizeActionTag(actionModel?.actionTagName || tag)
}

function catalogActionPriceUsd(action, options = {}) {
    if (!action) return null

    const { aiModels, quest, durationSeconds } = options || {}
    const models = Array.isArray(aiModels) ? aiModels : []
    const feeTag = actionEffectiveFeeTag(action, models)
    const model = findModelByActionTag(models, feeTag)
    if (!model) return null

    const duration = actionDurationSeconds(action, durationSeconds)
    const price = isPublicAiPricingContext(quest)
        ? getPublicModelCostWithDuration(model, duration)
        : getPrivateModelCostWithDuration(model, duration)

    return Number.isFinite(Number(price)) ? Number(price) : null
}

async function confirmRetryActionFee(action, exchangeRate, options = {}) {
    const catalogUsdCost = catalogActionPriceUsd(action, options)
    const actionUsdCost = catalogUsdCost ?? actionPriceUsd(action)
    if (actionUsdCost == null) return null
    if (actionUsdCost <= 0) return { confirmed: true, paymentMode: null }

    const usedCatalogPrice = catalogUsdCost != null
    const usdCost = usedCatalogPrice ? actionUsdCost : actionUsdCost / actionGenerationCount(action)

    const modelType = action?.model_type || action?.modelType || null
    const iconType =
        modelType === 'video' ? 'video-gen' : modelType === 'image' ? 'image-gen' : null
    const generationLabel =
        iconType === 'video-gen'
            ? 'Generate Video'
            : iconType === 'image-gen'
            ? 'Generate Image'
            : 'Generate'
    const currentUserHasFreeAiFeature =
        window?.gon?.currentUser?.features?.some?.(feature => feature.name === 'use_ai_for_free') ||
        false
    const sats = usdToSats(usdCost, exchangeRate)
    const effectiveModelFeeSats = currentUserHasFreeAiFeature && sats != null && sats > 0 ? 1 : sats
    const usdPriceStr = `$${usdCost.toFixed(2)}`
    const title =
        action?.display_name ||
        action?.displayName ||
        action?.preview_title ||
        action?.previewTitle ||
        action?.command_text ||
        action?.commandText ||
        action?.tag ||
        'AI generation'

    const choice = await confirmAiPaymentTray({
        eyebrow: generationLabel,
        title,
        usdPriceStr,
        iconType,
        effectiveModelFeeSats,
    })

    if (choice === 'bsv' || choice === 'stripe_metered') {
        return { confirmed: true, paymentMode: choice }
    }

    return { confirmed: false, paymentMode: null }
}

const ImageRetryButton = ({ answerId, answer, quest, children }) => {
    const [retryingAiRunId, setRetryingAiRunId] = useState(null)

    // Get pricing data from store
    const aiModels = useStore(state => state.aiModels)
    const exchangeRate = useStore(state => state.exchangeRate)

    useEffect(() => {
        if (!retryingAiRunId || retryingAiRunId === '__legacy__') return

        const retriedRun = answer?.ai_runs?.find(run => run?.id === retryingAiRunId)
        if (!retriedRun || retriedRun.status === 'failed') return

        setRetryingAiRunId(currentRunId => (currentRunId === retryingAiRunId ? null : currentRunId))
    }, [answer?.ai_runs, retryingAiRunId])

    const markAiRunRetrying = aiRunId => {
        if (!answer?.id || !aiRunId) return null

        const currentAnswer = cache.getCachedAnswer(answer.id) || answer
        if (!Array.isArray(currentAnswer?.ai_runs)) return null
        let didUpdate = false
        const nextAiRuns = currentAnswer.ai_runs.map(run => {
            if (run?.id !== aiRunId) return run

            didUpdate = true
            return {
                ...run,
                status: 'running',
                error_payload: {},
                errorPayload: {},
                result_payload: {},
                resultPayload: {},
                output_urls: [],
                outputUrls: [],
                updated_at: new Date().toISOString(),
            }
        })

        if (!didUpdate) return null

        const nextAnswer = {
            ...currentAnswer,
            image_generation_failed: false,
            image_generation_failure_reason: null,
            image_generation_failure_payload: null,
            ai_runs: nextAiRuns,
        }
        cache.cacheAnswer(nextAnswer)
        return currentAnswer
    }

    const handleRetry = async (ev, retryOptions = {}) => {
        ev.stopPropagation()
        ev.preventDefault()

        if (!answerId || retryingAiRunId) {
            return
        }

        // Parse action command from answer content
        const actionCommand =
            retryOptions.action?.tag ||
            retryOptions.actionTag ||
            parseActionCommandFromContent(answer?.content)

        if (!actionCommand) {
            console.error('Could not find action command in answer content')
            return
        }

        // Show charge confirmation
        const isPublic = isPublicAiPricingContext(quest)
        const actionFeeResult = await confirmRetryActionFee(retryOptions.action, exchangeRate, {
            aiModels,
            quest,
            durationSeconds: retryOptions.durationSeconds,
        })
        const paymentMode = actionFeeResult?.paymentMode || null
        const confirmed =
            actionFeeResult?.confirmed ??
            (await confirmActionCommandFee(
                actionCommand,
                isPublic,
                aiModels,
                exchangeRate,
                answer?.content,
                retryOptions.durationSeconds,
            ))

        if (!confirmed) {
            return // User cancelled
        }

        // Proceed with retry
        const aiRunId = retryOptions.aiRunId || null
        setRetryingAiRunId(aiRunId || '__legacy__')
        const previousAnswer = markAiRunRetrying(aiRunId)

        try {
            await api.retryImageGeneration(answerId, {
                ai_run_id: aiRunId,
                ...(paymentMode ? { payment_mode: paymentMode } : {}),
            })

            if (!aiRunId) setRetryingAiRunId(null)
        } catch (error) {
            if (previousAnswer) cache.cacheAnswer(previousAnswer)
            setRetryingAiRunId(null)
            console.error('Failed to retry image generation:', error)
        }
    }

    const isRetrying = !!retryingAiRunId
    const disabled = !answerId || isRetrying

    return children({ onClick: handleRetry, isRetrying, retryingAiRunId, disabled })
}

export const ImageGenerationFailureBubble = ({
    errorMessage,
    onRetry = null,
    isRetrying = false,
    retryDisabled = false,
    showRetry = true,
    onClear = null,
    isClearing = false,
    showClear = false,
    centered = false,
}) => {
    const openConfirmModal = useStore(state => state.actions.modalActions.openConfirmModal)

    const showErrorDetails = ev => {
        ev.stopPropagation()
        ev.preventDefault()
        const displayError = getDisplayAiGenerationError(errorMessage)
        const friendlyMessage = errorMessage ? (
            <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
                <p style={{ marginTop: 0, marginBottom: '1em' }}>
                    AI generations can sometimes fail for various reasons, most commonly because the
                    AI provider has deemed your prompt "unsafe" or sometimes just cause of temporary
                    network issues.
                </p>
                <p style={{ marginTop: 0, marginBottom: '1em' }}>
                    The AI provider sent us the information below which may help figure out what
                    happened. If this was a paid generation, don't worry you haven't been charged.
                    Feel free to try your generation again.
                </p>
                <p style={{ marginBottom: '0.5em', fontWeight: 600 }}>
                    Details from the AI provider:
                </p>
                <div
                    style={{
                        marginTop: 0,
                        padding: '12px',
                        background: 'rgba(0,0,0,0.03)',
                        borderRadius: '4px',
                        fontSize: '0.98em',
                    }}
                >
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>{displayError.title}</div>
                    <div>
                        {displayError.summary || 'The provider did not send a readable error.'}
                    </div>
                </div>
            </div>
        ) : (
            <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
                <p style={{ marginTop: 0, marginBottom: '1em' }}>
                    AI generations can sometimes fail for various reasons, most commonly because the
                    AI provider has deemed your prompt "unsafe" or sometimes just cause of temporary
                    network issues.
                </p>
                <p style={{ marginTop: 0, marginBottom: 0 }}>
                    If this was a paid generation, don't worry you haven't been charged. Feel free
                    to try your generation again.
                </p>
            </div>
        )
        openConfirmModal(friendlyMessage, 'Got it', () => {}, {
            iconType: 'info',
            hideCancel: true,
        })
    }

    return (
        <div
            className={cn(
                errorBubbleStyles.errorContainer,
                centered && errorBubbleStyles.centeredErrorContainer,
            )}
            onClick={ev => {
                ev.stopPropagation()
                ev.preventDefault()
            }}
        >
            <div className={errorBubbleStyles.errorText}>AI Generation failed</div>
            {showRetry && (
                <button
                    className={errorBubbleStyles.infoButton}
                    onClick={onRetry}
                    aria-label="Retry image generation"
                    disabled={retryDisabled || !onRetry}
                    style={{ opacity: isRetrying ? 0.5 : 1 }}
                >
                    <RefreshCw
                        size={14}
                        strokeWidth={2.5}
                        style={{
                            animation: isRetrying ? 'spin 1s linear infinite' : 'none',
                        }}
                    />
                </button>
            )}
            <button
                className={errorBubbleStyles.infoButton}
                onClick={showErrorDetails}
                aria-label="View error details"
            >
                <Info size={14} strokeWidth={2.5} />
            </button>
            {showClear && onClear && (
                <WithTooltip tip="Cancel retry and keep original image">
                    <button
                        className={errorBubbleStyles.infoButton}
                        onClick={onClear}
                        aria-label="Cancel retry"
                        disabled={isClearing}
                        style={{ opacity: isClearing ? 0.5 : 1 }}
                    >
                        <X size={14} strokeWidth={2.5} />
                    </button>
                </WithTooltip>
            )}
        </div>
    )
}

ImageRetryButton.ErrorBubble = ({
    answerId,
    errorMessage,
    answer,
    quest,
    isAnswerOwner = true,
}) => {
    const [isClearing, setIsClearing] = useState(false)

    // Determine if this is a retry failure (has existing image)
    const hasExistingImage = !!answer?.answer_image_url

    const handleClearError = async ev => {
        ev.stopPropagation()
        ev.preventDefault()

        if (isClearing) return

        setIsClearing(true)

        try {
            await api.clearImageGenerationError(answerId)
        } catch (error) {
            console.error('Failed to clear image generation error:', error)
        } finally {
            setIsClearing(false)
        }
    }

    return (
        <ImageRetryButton answerId={answerId} answer={answer} quest={quest}>
            {({ onClick, isRetrying, disabled }) => (
                <ImageGenerationFailureBubble
                    errorMessage={errorMessage}
                    onRetry={isAnswerOwner ? onClick : undefined}
                    isRetrying={isRetrying}
                    retryDisabled={disabled}
                    showRetry={isAnswerOwner}
                    onClear={handleClearError}
                    isClearing={isClearing}
                    showClear={hasExistingImage}
                />
            )}
        </ImageRetryButton>
    )
}

ImageRetryButton.Overlay = ({ answerId, answer, quest }) => {
    return (
        <ImageRetryButton answerId={answerId} answer={answer} quest={quest}>
            {({ onClick, isRetrying, disabled }) => (
                <div
                    className={styles.retryButtonOverlay}
                    onClick={onClick}
                    aria-label="Retry image generation"
                    style={{
                        opacity: disabled ? 0.2 : undefined,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                >
                    <i
                        className={`fa fa-refresh ${isRetrying ? 'fa-spin' : ''}`}
                        style={{
                            fontSize: '16px',
                        }}
                    />
                </div>
            )}
        </ImageRetryButton>
    )
}

export default ImageRetryButton
